/**
 * Absences (docs/OSEBJE.md §10), mirroring `pkg/enums/StaffAbsence*` on the API side.
 *
 * Written out here rather than read off the wire for the reason `enums/staffTime.ts`
 * gives: every one of them needs a Slovenian word beside it, and a label assembled from
 * a wire name is a key `yarn check-i18n` cannot see.
 */

/**
 * Why somebody was not at work on a day the record would otherwise leave empty.
 *
 * **The list is OURS and it is PROVISIONAL.** ZEPDSV prescribes no closed catalogue of
 * absence names — what it prescribes is who carries the allowance — so every name here
 * was chosen by us, and §10 asks that every screen listing them SAYS so until an
 * accountant has confirmed the mapping (§16, question 1). `STAFF_ABSENCE_KINDS_ARE_PROVISIONAL`
 * below is the flag the screens say it with.
 *
 * There is deliberately no payer and no payroll code anywhere near this list: this
 * product builds the time record, and payroll is done by accounting in another program.
 */
export enum StaffAbsenceKind {
    /** Annual leave — the ONE kind somebody asks for and a manager approves. */
    Vacation = 'vacation',

    /** Illness. Reported and recorded, never applied for. */
    SickLeave = 'sick_leave',

    /** A public holiday, normally derived from the person's own calendar rather than stored. */
    Holiday = 'holiday',

    Training     = 'training',
    BusinessTrip = 'business_trip',

    /** A finding: expected, did not come, said nothing. Corrected by superseding, never deleted. */
    Unexcused = 'unexcused',

    /** Everything the list does not name, with the note carrying what it was. */
    Other = 'other',
}

/**
 * Whether the list above may be presented as final. It may not (§10) — and a screen that
 * lists the kinds has to say so out loud rather than leave somebody to find out from an
 * accountant.
 */
export const STAFF_ABSENCE_KINDS_ARE_PROVISIONAL = true

/** The kinds a person may ask for on their own behalf. One, and §10 explains why. */
export const STAFF_REQUESTABLE_ABSENCE_KINDS: string[] = [StaffAbsenceKind.Vacation]

/**
 * How many days one REQUEST may cover — `DataStructures.StaffAbsenceRequestMaxRangeDays`.
 *
 * Two months, and the ceiling is checked here as well as on the server for the reason the
 * backwards-range check is: the thing it actually catches is a `to` typed as 2027 rather
 * than 2026, and a person who has just filled in a form is owed that before a round trip
 * rather than as a refusal afterwards.
 *
 * A MANAGER's entry is bounded by a year instead — a long absence genuinely arrives by
 * that road — so this constant belongs to the request dialogue alone.
 */
export const STAFF_ABSENCE_REQUEST_MAX_DAYS = 62

/**
 * Where a request has got to.
 *
 * A REQUEST has states; a RECORD does not — it is the fact that somebody was away, and
 * the only thing that ever happens to it is being superseded (§10).
 */
export enum StaffAbsenceState {
    Requested = 'requested',
    Approved  = 'approved',
    Rejected  = 'rejected',
    Withdrawn = 'withdrawn',
}

/**
 * Which door one day of absence came into the record through. It is on the record for the
 * reason a stamp carries its source: a year later, "she asked and it was granted", "the
 * supervisor wrote it down" and "the country was shut" are what make the day explicable.
 */
export enum StaffAbsenceSource {
    Request  = 'request',
    Manager  = 'manager',

    /** A public holiday, worked out from the calendar rather than stored. */
    Calendar = 'calendar',
}
